/* Created by Interactive 63 - https://interactive63.com */
/**
 * Adding, duplicating, removing and rearranging whole layers in a project that is already open:
 * bringing another SVG in as a new group, copying elements with their animation, deleting
 * elements together with everything inside them, and moving, grouping and ungrouping them. Each changes the SVG source, the element table and the
 * tracks together, so they stay consistent. All work on an Immer draft, so the caller decides how
 * the change is recorded.
 */
import { prefixIds } from '../compiler/prefix-ids';
import { CASCADING_PROPERTIES } from '../properties/cascade';
import { bakeBlocker, bakeTransform, splitPathElement } from './simplify';
import {
  IDENTITY,
  formatTransform,
  invert,
  multiply,
  parseTransform,
  type Matrix,
} from '../geometry/matrix';
import { createId, elementIdFromIndex } from '../model/ids';
import {
  CONTAINER_TAGS,
  type ElementId,
  type ElementMeta,
  type Project,
  type Track,
} from '../model/types';

const SVG_NS = 'http://www.w3.org/2000/svg';
/** Presentation attributes on the imported root that its content inherits, so they move to the group. */
const INHERITED_ATTRIBUTES = [
  'fill',
  'fill-rule',
  'clip-rule',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-miterlimit',
  'stroke-dasharray',
  'color',
  'font-family',
  'font-size',
  'font-weight',
  'opacity',
];

/** Parses SVG source into a document. */
function parse(source: string): Document {
  return new DOMParser().parseFromString(source, 'image/svg+xml');
}

/** The number in an element ID such as "e12". */
function indexOf(id: ElementId): number {
  return Number(id.slice(1));
}

/** The highest numbered element ID in use, or zero when there are none. */
function highestIndex(elements: Record<ElementId, ElementMeta>): number {
  return Object.keys(elements).reduce((highest, id) => Math.max(highest, indexOf(id)), 0);
}

/**
 * Adds an imported SVG to the open project as a new top-level group at its native size. Element
 * IDs are renumbered after the existing ones, and the ids inside the artwork get a prefix so
 * gradients and clip paths cannot clash with the ones already there. Returns the group's ID.
 */
export function addImportedLayer(draft: Project, imported: Project): ElementId {
  const target = parse(draft.svg.source);
  const incoming = parse(imported.svg.source);
  const firstIndex = highestIndex(draft.elements) + 1;
  const groupId = elementIdFromIndex(firstIndex);

  const renamed = new Map<ElementId, ElementId>();
  let next = firstIndex + 1;
  for (const oldId of Object.keys(imported.elements).sort((a, b) => indexOf(a) - indexOf(b))) {
    renamed.set(oldId, elementIdFromIndex(next));
    next += 1;
  }

  const group = target.createElementNS(SVG_NS, 'g');
  group.setAttribute('data-st-id', groupId);
  const { viewBox } = imported.svg;
  if (viewBox.x !== 0 || viewBox.y !== 0) {
    group.setAttribute('transform', `translate(${-viewBox.x} ${-viewBox.y})`);
  }
  for (const name of INHERITED_ATTRIBUTES) {
    const value = incoming.documentElement.getAttribute(name);
    if (value !== null) group.setAttribute(name, value);
  }
  for (const child of Array.from(incoming.documentElement.children)) {
    group.append(target.importNode(child, true));
  }
  for (const el of Array.from(group.querySelectorAll('[data-st-id]'))) {
    const oldId = el.getAttribute('data-st-id') ?? '';
    el.setAttribute('data-st-id', renamed.get(oldId) ?? oldId);
  }
  prefixIds(group, `l${firstIndex}-`);
  target.documentElement.append(group);
  draft.svg.source = new XMLSerializer().serializeToString(target.documentElement);

  const topLevel = new Set(imported.rootIds);
  for (const [oldId, meta] of Object.entries(imported.elements)) {
    const id = renamed.get(oldId) ?? oldId;
    const parent = meta.parentId === null ? undefined : renamed.get(meta.parentId);
    draft.elements[id] = {
      ...meta,
      id,
      parentId: topLevel.has(oldId) ? groupId : (parent ?? null),
      childIds: meta.childIds.map((child) => renamed.get(child) ?? child),
    };
  }
  draft.elements[groupId] = {
    id: groupId,
    tag: 'g',
    label: imported.name,
    parentId: null,
    childIds: imported.rootIds.map((id) => renamed.get(id) ?? id),
    origin: { x: 50, y: 50 },
    excludeFromExport: false,
    hiddenInEditor: false,
  };
  draft.rootIds.push(groupId);
  return groupId;
}

/** Every ID in the list together with the IDs of everything inside those elements. */
function withDescendants(
  elements: Record<ElementId, ElementMeta>,
  ids: readonly ElementId[],
): Set<ElementId> {
  const all = new Set<ElementId>();
  /** Adds an element and then its children, skipping anything already visited. */
  const visit = (id: ElementId): void => {
    const meta = elements[id];
    if (!meta || all.has(id)) return;
    all.add(id);
    meta.childIds.forEach(visit);
  };
  ids.forEach(visit);
  return all;
}

/**
 * Deletes elements and everything inside them: their markup, table entries and tracks. Returns
 * the IDs that were removed, so the caller can tidy the selection.
 */
export function removeElements(draft: Project, ids: readonly ElementId[]): ElementId[] {
  const doomed = withDescendants(draft.elements, ids);
  if (doomed.size === 0) return [];

  const doc = parse(draft.svg.source);
  for (const id of doomed) {
    doc.querySelector(`[data-st-id="${id}"]`)?.remove();
  }
  draft.svg.source = new XMLSerializer().serializeToString(doc.documentElement);

  for (const id of doomed) {
    const parentId = draft.elements[id]?.parentId;
    const parent = parentId ? draft.elements[parentId] : undefined;
    if (parent && !doomed.has(parent.id)) {
      parent.childIds = parent.childIds.filter((child) => child !== id);
    }
  }
  draft.rootIds = draft.rootIds.filter((id) => !doomed.has(id));
  draft.tracks = draft.tracks.filter((track) => !doomed.has(track.elementId));
  draft.elements = Object.fromEntries(
    Object.entries(draft.elements).filter(([id]) => !doomed.has(id)),
  );
  return [...doomed];
}

/** A deep, detached copy of plain project data, safe to take from an Immer draft. */
function cloneData<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** A trailing copy number such as the "_3" in "star_3". */
const COPY_NUMBER = /_(\d+)$/;

/**
 * The name for a copy: the original's base name with the next free number, so copying "star" or
 * "star_2" gives "star_3" when "star_2" already exists, rather than stacking suffixes.
 */
function numberedLabel(elements: Record<ElementId, ElementMeta>, label: string): string {
  const base = label.replace(COPY_NUMBER, '');
  let highest = 1;
  for (const meta of Object.values(elements)) {
    if (meta.label === base) continue;
    const match = COPY_NUMBER.exec(meta.label);
    if (match && meta.label.slice(0, match.index) === base) {
      highest = Math.max(highest, Number(match[1]));
    }
  }
  return `${base}_${highest + 1}`;
}

/** The IDs that exist and are not inside another listed element, so nothing is copied twice. */
function outermost(
  elements: Record<ElementId, ElementMeta>,
  ids: readonly ElementId[],
): ElementId[] {
  const listed = new Set(ids);
  return [...listed].filter((id) => {
    if (!elements[id]) return false;
    for (let parent = elements[id].parentId; parent; parent = elements[parent]?.parentId ?? null) {
      if (listed.has(parent)) return false;
    }
    return true;
  });
}

/**
 * Duplicates elements together with everything inside them and their animation. Each copy sits
 * directly after its original, gets fresh element IDs after the existing ones, and has the ids in
 * its markup prefixed so gradients and clip paths inside it cannot clash with the original's.
 * Returns the IDs of the new top-level copies.
 */
export function duplicateElements(draft: Project, ids: readonly ElementId[]): ElementId[] {
  const sources = outermost(draft.elements, ids);
  if (sources.length === 0) return [];

  const doc = parse(draft.svg.source);
  let next = highestIndex(draft.elements) + 1;
  const copies: ElementId[] = [];
  const trackCopies: Track[] = [];

  for (const sourceId of sources) {
    const original = doc.querySelector(`[data-st-id="${sourceId}"]`);
    if (!original) continue;
    const copyId = elementIdFromIndex(next);
    const renamed = new Map<ElementId, ElementId>();
    for (const id of withDescendants(draft.elements, [sourceId])) {
      renamed.set(id, elementIdFromIndex(next));
      next += 1;
    }

    const clone = original.cloneNode(true) as Element;
    for (const el of [clone, ...Array.from(clone.querySelectorAll('[data-st-id]'))]) {
      const renamedId = renamed.get(el.getAttribute('data-st-id') ?? '');
      if (renamedId) el.setAttribute('data-st-id', renamedId);
    }
    prefixIds(clone, `d${indexOf(copyId)}-`);
    original.after(clone);

    for (const [oldId, newId] of renamed) {
      const meta = draft.elements[oldId];
      if (!meta) continue;
      const isTop = oldId === sourceId;
      draft.elements[newId] = {
        ...cloneData(meta),
        id: newId,
        label: isTop ? numberedLabel(draft.elements, meta.label) : meta.label,
        parentId: isTop ? meta.parentId : (renamed.get(meta.parentId ?? '') ?? null),
        childIds: meta.childIds.map((child) => renamed.get(child) ?? child),
      };
    }

    const parentId = draft.elements[sourceId]?.parentId ?? null;
    const siblings = parentId ? draft.elements[parentId]?.childIds : draft.rootIds;
    siblings?.splice(siblings.indexOf(sourceId) + 1, 0, copyId);

    for (const track of draft.tracks) {
      const elementId = renamed.get(track.elementId);
      if (!elementId) continue;
      const copy = cloneData(track);
      trackCopies.push({
        ...copy,
        id: createId('tr'),
        elementId,
        keyframes: copy.keyframes.map((keyframe) => ({ ...keyframe, id: createId('kf') })),
      });
    }
    copies.push(copyId);
  }

  draft.tracks.push(...trackCopies);
  draft.svg.source = new XMLSerializer().serializeToString(doc.documentElement);
  return copies;
}

/** Inherited attributes that do not compose with a parent's, so moving a layer cannot lose them. */
const MOVE_INHERITED = INHERITED_ATTRIBUTES.filter((name) => name !== 'opacity');

/** What an inherited attribute resolves to when nothing in the chain sets it. */
const INHERITED_DEFAULTS: Record<string, string> = {
  fill: 'black',
  'fill-rule': 'nonzero',
  'clip-rule': 'nonzero',
  stroke: 'none',
  'stroke-width': '1',
  'stroke-linecap': 'butt',
  'stroke-linejoin': 'miter',
  'stroke-miterlimit': '4',
  'stroke-dasharray': 'none',
};

/** Attributes on a group that cannot be handed down to its children when it is dissolved. */
const UNSPREADABLE_ATTRIBUTES = ['clip-path', 'mask', 'filter'];

export interface MoveTarget {
  /** The group to move into, or null for the top level. */
  parentId: ElementId | null;
  /** Position among the destination's children once the moved elements have left their old places. */
  index: number;
}

export interface MoveResult {
  moved: ElementId[];
  /** Why nothing moved, in plain language. */
  reason?: string;
}

/** Every element ID in document order. */
function documentOrder(draft: Project): ElementId[] {
  const order: ElementId[] = [];
  const visit = (id: ElementId): void => {
    order.push(id);
    draft.elements[id]?.childIds.forEach(visit);
  };
  draft.rootIds.forEach(visit);
  return order;
}

/** Whether `id` sits anywhere inside `ancestorId`. */
function isInside(
  elements: Record<ElementId, ElementMeta>,
  id: ElementId,
  ancestorId: ElementId,
): boolean {
  for (let p = elements[id]?.parentId; p; p = elements[p]?.parentId ?? null) {
    if (p === ancestorId) return true;
  }
  return false;
}

/** The ancestors of a node below the root, nearest first. */
function ancestorsOf(node: Element): Element[] {
  const chain: Element[] = [];
  const root = node.ownerDocument.documentElement;
  for (let p = node.parentElement; p && p !== root; p = p.parentElement) chain.push(p);
  return chain;
}

/** The combined static transform of a node's ancestors, outermost applied first. */
function ancestorMatrix(node: Element): Matrix {
  return ancestorsOf(node)
    .reverse()
    .reduce<Matrix>(
      (total, el) => multiply(total, parseTransform(el.getAttribute('transform')) ?? IDENTITY),
      IDENTITY,
    );
}

/** The value an inherited attribute has for a node's content, or null when no ancestor sets it. */
function inheritedValue(node: Element, name: string): string | null {
  for (const ancestor of ancestorsOf(node)) {
    const value = ancestor.getAttribute(name);
    if (value !== null) return value;
  }
  return null;
}

/** Whether any ancestor of an element has animation that moves it, which a move would lose. */
export function hasAnimatedTransformAncestor(project: Project, id: ElementId): boolean {
  const moving = new Set(['translate', 'rotate', 'scale', 'skew']);
  for (let p = project.elements[id]?.parentId; p; p = project.elements[p]?.parentId ?? null) {
    if (project.tracks.some((t) => t.elementId === p && t.enabled && moving.has(t.property))) {
      return true;
    }
  }
  return false;
}

/**
 * Moves elements to a new parent and position. Each keeps its place on the artboard: the static
 * transforms of the old and new ancestors are compared and the difference is written to the
 * element's own transform, and inherited paint (fill, stroke and so on) is copied onto it where
 * the new parent would change it. Animation follows because tracks refer to element IDs. Moving
 * a group into itself is refused.
 */
export function moveElements(
  draft: Project,
  ids: readonly ElementId[],
  target: MoveTarget,
): MoveResult {
  const wanted = new Set(outermost(draft.elements, ids));
  const moving = documentOrder(draft).filter((id) => wanted.has(id));
  if (moving.length === 0) return { moved: [] };

  if (target.parentId !== null) {
    const destination = draft.elements[target.parentId];
    if (!destination) return { moved: [], reason: 'That destination no longer exists.' };
    if (!CONTAINER_TAGS.includes(destination.tag)) {
      return { moved: [], reason: 'Only groups can hold other layers.' };
    }
    const intoSelf = moving.some(
      (id) => id === target.parentId || isInside(draft.elements, target.parentId ?? '', id),
    );
    if (intoSelf) return { moved: [], reason: 'A group cannot be moved into itself.' };
  }

  const doc = parse(draft.svg.source);
  const nodeOf = (id: ElementId): Element | null => doc.querySelector(`[data-st-id="${id}"]`);
  const before = moving.flatMap((id) => {
    const node = nodeOf(id);
    if (!node) return [];
    return [
      {
        id,
        node,
        matrix: ancestorMatrix(node),
        inherited: MOVE_INHERITED.map((name) => [name, inheritedValue(node, name)] as const),
      },
    ];
  });
  if (before.length === 0) return { moved: [] };

  for (const { id } of before) {
    const oldParentId = draft.elements[id]?.parentId;
    const list = oldParentId ? draft.elements[oldParentId]?.childIds : draft.rootIds;
    if (list) list.splice(list.indexOf(id), 1);
  }
  const siblings = target.parentId ? draft.elements[target.parentId]?.childIds : draft.rootIds;
  if (!siblings) return { moved: [] };
  const index = Math.max(0, Math.min(target.index, siblings.length));
  const reference = siblings[index] ? nodeOf(siblings[index]) : null;
  const parentNode = target.parentId ? nodeOf(target.parentId) : doc.documentElement;
  if (!parentNode) return { moved: [] };
  siblings.splice(index, 0, ...before.map((b) => b.id));

  for (const { id, node, matrix, inherited } of before) {
    parentNode.insertBefore(node, reference);
    const element = draft.elements[id];
    if (element) element.parentId = target.parentId;

    const next = invert(ancestorMatrix(node));
    const own = parseTransform(node.getAttribute('transform'));
    if (next && own) {
      const text = formatTransform(multiply(next, multiply(matrix, own)));
      if (text === '') node.removeAttribute('transform');
      else node.setAttribute('transform', text);
    }
    for (const [name, oldValue] of inherited) {
      if (node.hasAttribute(name)) continue;
      const newValue = inheritedValue(node, name);
      const wasValue = oldValue ?? INHERITED_DEFAULTS[name];
      if (wasValue !== undefined && (newValue ?? INHERITED_DEFAULTS[name]) !== wasValue) {
        node.setAttribute(name, wasValue);
      }
    }
  }
  draft.svg.source = new XMLSerializer().serializeToString(doc.documentElement);
  return { moved: before.map((b) => b.id) };
}

/**
 * Wraps elements in a new group that takes the place of the topmost one (the last in document
 * order), inside their shared parent or at the top level when they have none in common. Returns
 * the new group's ID, or null when there is nothing to group.
 */
export function groupElements(draft: Project, ids: readonly ElementId[]): ElementId | null {
  const wanted = new Set(outermost(draft.elements, ids));
  const moving = documentOrder(draft).filter((id) => wanted.has(id));
  const last = moving[moving.length - 1];
  if (last === undefined) return null;

  const shared = new Set(moving.map((id) => draft.elements[id]?.parentId ?? null)).size === 1;
  const parentId = shared ? (draft.elements[last]?.parentId ?? null) : null;
  // The group sits straight after this element, which is `last` itself or its top-level ancestor.
  let anchorId = last;
  while (!shared && draft.elements[anchorId]?.parentId) {
    anchorId = draft.elements[anchorId]?.parentId ?? anchorId;
  }

  const doc = parse(draft.svg.source);
  const anchor = doc.querySelector(`[data-st-id="${anchorId}"]`);
  if (!anchor) return null;
  const groupId = elementIdFromIndex(highestIndex(draft.elements) + 1);
  const group = doc.createElementNS(SVG_NS, 'g');
  group.setAttribute('data-st-id', groupId);
  anchor.after(group);
  draft.svg.source = new XMLSerializer().serializeToString(doc.documentElement);

  draft.elements[groupId] = {
    id: groupId,
    tag: 'g',
    label: 'Group',
    parentId,
    childIds: [],
    origin: { x: 50, y: 50 },
    excludeFromExport: false,
    hiddenInEditor: false,
  };
  const list = parentId ? draft.elements[parentId]?.childIds : draft.rootIds;
  list?.splice(list.indexOf(anchorId) + 1, 0, groupId);
  moveElements(draft, moving, { parentId: groupId, index: 0 });
  return groupId;
}

export interface UngroupResult {
  /** The children that were released, in order. */
  freed: ElementId[];
  /** Why some or all groups were left alone, in plain language. */
  reason?: string;
}

/** The reason a group cannot be dissolved, or null when it can. */
function ungroupBlocker(draft: Project, id: ElementId, node: Element | null): string | null {
  if (draft.tracks.some((track) => track.elementId === id)) {
    return 'This group is animated. Remove its animation first, or keep the group.';
  }
  if (!node) return null;
  const opacity = node.getAttribute('opacity');
  if (
    UNSPREADABLE_ATTRIBUTES.some((name) => node.hasAttribute(name)) ||
    (opacity !== null && Number(opacity) !== 1)
  ) {
    return 'This group has opacity, a clip, a mask or a filter that cannot be passed to its contents.';
  }
  return null;
}

/**
 * Dissolves groups, putting their children where the group was. The group's own transform and
 * inherited paint are carried onto the children so nothing moves or changes colour. Animated
 * groups, and groups whose opacity, clip, mask or filter cannot be spread, are left alone.
 */
export function ungroupElements(draft: Project, ids: readonly ElementId[]): UngroupResult {
  const freed: ElementId[] = [];
  let reason: string | undefined;
  const groups = documentOrder(draft).filter(
    (id) => ids.includes(id) && draft.elements[id]?.tag === 'g',
  );
  if (groups.length === 0) return { freed, reason: 'Select a group to ungroup.' };

  for (const id of groups.reverse()) {
    const meta = draft.elements[id];
    if (!meta) continue;
    const doc = parse(draft.svg.source);
    const blocker = ungroupBlocker(draft, id, doc.querySelector(`[data-st-id="${id}"]`));
    if (blocker) {
      reason ??= blocker;
      continue;
    }
    const siblings = meta.parentId ? draft.elements[meta.parentId]?.childIds : draft.rootIds;
    const children = [...meta.childIds];
    const result = moveElements(draft, children, {
      parentId: meta.parentId,
      index: Math.max(0, siblings?.indexOf(id) ?? 0),
    });
    freed.push(...result.moved);
    removeElements(draft, [id]);
  }
  return { freed, ...(reason === undefined ? {} : { reason }) };
}

export interface SplitLayerResult {
  /** The new part layers, in order. The original ID now belongs to the group around them. */
  parts: ElementId[];
  /** Why the layer was not split, in plain language. */
  reason?: string;
}

/** Animation that only makes sense on one shape, so it cannot move to a group of parts. */
const SHAPE_ONLY_PROPERTIES = new Set<string>(['draw', 'morph', ...CASCADING_PROPERTIES]);

/**
 * Splits a compound path layer into one layer per shape, inside a group that keeps the original
 * layer's ID, name, position, opacity and animation. Holes stay with the shape around them.
 * Refused when the layer has animation that only a single shape can have.
 */
export function splitPathLayer(draft: Project, id: ElementId): SplitLayerResult {
  const meta = draft.elements[id];
  if (!meta || meta.tag !== 'path') {
    return { parts: [], reason: 'Only paths can be split into parts.' };
  }
  if (draft.tracks.some((t) => t.elementId === id && SHAPE_ONLY_PROPERTIES.has(t.property))) {
    return {
      parts: [],
      reason: 'This layer has shape animation (stroke reveal, morph or colour). Remove it first.',
    };
  }
  const doc = parse(draft.svg.source);
  const node = doc.querySelector(`[data-st-id="${id}"]`);
  if (!node) return { parts: [] };
  const outcome = splitPathElement(node, true);
  if (outcome === null || outcome === 'too-many' || !outcome.wrapper) {
    return { parts: [], reason: 'This path is a single shape, so there is nothing to split.' };
  }

  outcome.wrapper.setAttribute('data-st-id', id);
  let next = highestIndex(draft.elements) + 1;
  const parts = outcome.parts.map((part, index) => {
    const partId = elementIdFromIndex(next);
    next += 1;
    part.setAttribute('data-st-id', partId);
    draft.elements[partId] = {
      id: partId,
      tag: 'path',
      label: `${meta.label} ${index + 1}`,
      parentId: id,
      childIds: [],
      origin: { x: 50, y: 50 },
      excludeFromExport: false,
      hiddenInEditor: false,
    };
    return partId;
  });
  meta.tag = 'g';
  meta.childIds = parts;
  delete meta.pathLength;
  draft.svg.source = new XMLSerializer().serializeToString(doc.documentElement);
  return { parts };
}

export interface FlattenResult {
  done: ElementId[];
  /** Why layers were left alone, in plain language. */
  reason?: string;
}

/**
 * Folds each path layer's own transform into its geometry so it starts from clean coordinates.
 * Layers where that would change how they look are left alone, and the first reason is returned.
 */
export function flattenLayerTransforms(draft: Project, ids: readonly ElementId[]): FlattenResult {
  const doc = parse(draft.svg.source);
  const done: ElementId[] = [];
  let reason: string | undefined;
  for (const id of ids) {
    const node = doc.querySelector(`[data-st-id="${id}"]`);
    if (!node) continue;
    if (!node.hasAttribute('transform')) {
      reason ??= 'This layer has no transform to flatten.';
      continue;
    }
    const blocker = bakeBlocker(node);
    if (blocker) {
      reason ??= blocker;
      continue;
    }
    if (bakeTransform(node)) {
      done.push(id);
      const meta = draft.elements[id];
      if (meta) delete meta.pathLength;
    }
  }
  if (done.length > 0)
    draft.svg.source = new XMLSerializer().serializeToString(doc.documentElement);
  return { done, ...(reason === undefined ? {} : { reason }) };
}
